import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MemoryItem, VisitorInfo } from '../types';
import {
  X,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Calendar,
  Sparkles,
} from 'lucide-react';
import { formatDateSpanish } from '../utils/storage';

interface MediaLightboxProps {
  memory: MemoryItem | null;
  initialIndex?: number;
  allMemories?: MemoryItem[];
  currentVisitor?: VisitorInfo | null;
  onClose: () => void;
  onNavigate?: (memory: MemoryItem) => void;
}

export const MediaLightbox: React.FC<MediaLightboxProps> = ({
  memory,
  initialIndex = 0,
  currentVisitor,
  onClose,
}) => {
  const [photoIndex, setPhotoIndex] = useState(initialIndex || 0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');

  // Swipe táctil para móviles
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchEndRef = useRef<{ x: number; y: number } | null>(null);

  // Resetear foto cuando cambia de publicación o initialIndex
  useEffect(() => {
    setPhotoIndex(initialIndex || 0);
  }, [memory?.id, initialIndex]);

  const mediaList = useMemo(() => {
    if (!memory) return [];
    if (memory.mediaUrls && memory.mediaUrls.length > 0) {
      return memory.mediaUrls;
    }
    return [memory.mediaUrl];
  }, [memory]);

  const hasMultiplePhotos = mediaList.length > 1;
  const currentPhotoUrl = mediaList[photoIndex] || memory?.mediaUrl;

  // Solo navegar entre fotos DE ESTA MISMA publicación
  const canGoPrev = photoIndex > 0;
  const canGoNext = photoIndex < mediaList.length - 1;

  const handlePrev = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (photoIndex > 0) {
      setSlideDirection('left');
      setPhotoIndex((prev) => prev - 1);
    }
  };

  const handleNext = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (photoIndex < mediaList.length - 1) {
      setSlideDirection('right');
      setPhotoIndex((prev) => prev + 1);
    }
  };

  // Controles de teclado restringidos a esta publicación
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!memory) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && canGoPrev) handlePrev();
      if (e.key === 'ArrowRight' && canGoNext) handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [memory, canGoPrev, canGoNext, photoIndex]);

  // Touch handlers para gestos táctiles en móviles
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
    touchEndRef.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    const diffX = touchStartRef.current.x - touchEndRef.current.x;
    const diffY = touchStartRef.current.y - touchEndRef.current.y;
    const minSwipe = 45;

    // Deslizar hacia abajo para cerrar (gesto natural)
    if (diffY < -80 && Math.abs(diffX) < 60) {
      onClose();
      return;
    }

    // Deslizar horizontal para navegar fotos dentro de esta publicación
    if (Math.abs(diffX) > minSwipe && Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX > 0 && canGoNext) {
        handleNext();
      } else if (diffX < 0 && canGoPrev) {
        handlePrev();
      }
    }
  };

  if (!memory) return null;

  const authorRole = memory.authorRole;
  const isVideo =
    memory.type === 'video' ||
    (typeof currentPhotoUrl === 'string' && currentPhotoUrl.startsWith('data:video'));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        className="fixed inset-0 z-50 flex flex-col justify-between bg-slate-950/95 backdrop-blur-2xl select-none touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* RESPLANDOR AMBIENTAL SUTIL DE FONDO (Toma el tono de la foto) */}
        {typeof currentPhotoUrl === 'string' && !isVideo && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-25 blur-3xl scale-125 pointer-events-none transition-all duration-700"
            style={{ backgroundImage: `url(${currentPhotoUrl})` }}
          />
        )}

        {/* 1. BARRA SUPERIOR MODERNA Y ELEGANTE */}
        <header className="relative z-30 flex items-center justify-between px-4 sm:px-8 pt-4 pb-3 sm:pt-5 w-full max-w-7xl mx-auto">
          {/* Autor y Ubicación */}
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md border shadow-xs shrink-0 ${
                authorRole === 'novia'
                  ? 'bg-rose-500/25 text-rose-200 border-rose-400/40'
                  : authorRole === 'novio'
                  ? 'bg-sky-500/25 text-sky-200 border-sky-400/40'
                  : 'bg-amber-500/25 text-amber-200 border-amber-400/40'
              }`}
            >
              <span>{authorRole === 'novia' ? '💖' : authorRole === 'novio' ? '💙' : '✨'}</span>
              <span className="truncate">
                {memory.authorName ||
                  (authorRole === 'novia' ? 'Laura' : authorRole === 'novio' ? 'Jackson' : 'Nuestra Historia')}
              </span>
            </span>

            {/* Fecha y Lugar compactos */}
            <div className="hidden sm:flex items-center gap-2 text-xs text-slate-300">
              {memory.place && (
                <span className="flex items-center gap-1 text-slate-300 truncate max-w-[180px]">
                  <MapPin className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                  <span className="truncate">{memory.place}</span>
                </span>
              )}
              {memory.date && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Calendar className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span>{formatDateSpanish(memory.date)}</span>
                </span>
              )}
            </div>
          </div>

          {/* Contador central de fotos de esta publicación */}
          {hasMultiplePhotos ? (
            <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] sm:text-xs font-semibold text-white/90 shadow-xs">
              <span className="text-amber-300 font-medium">
                Foto {photoIndex + 1} de {mediaList.length}
              </span>
            </div>
          ) : (
            <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] sm:text-xs font-semibold text-white/90 shadow-xs">
              <span>{isVideo ? 'Video' : 'Fotografía'}</span>
            </div>
          )}

          {/* Botón de Cerrar */}
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 active:scale-95 text-white backdrop-blur-md border border-white/15 transition-all cursor-pointer shadow-lg ml-2"
            title="Cerrar visor (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* 2. ÁREA CENTRAL: VISOR DE FOTOGRAFÍA O VIDEO DE ALTA CALIDAD */}
        <main className="relative z-20 flex-1 w-full flex items-center justify-center p-2 sm:p-6 overflow-hidden">
          {/* Botón Anterior Flotante en los bordes (No invade la foto) */}
          {canGoPrev && (
            <button
              onClick={handlePrev}
              className="absolute left-2 sm:left-6 z-30 w-11 h-11 sm:w-13 sm:h-13 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/70 active:scale-95 text-white/90 hover:text-white backdrop-blur-xl border border-white/20 shadow-2xl transition-all cursor-pointer group"
              title="Foto anterior (←)"
            >
              <ChevronLeft className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" />
            </button>
          )}

          {/* Imagen / Video Centrado en su Proporción Real */}
          <motion.div
            key={`${memory.id}-${photoIndex}`}
            initial={{ opacity: 0, scale: 0.96, x: slideDirection === 'right' ? 24 : -24 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="relative max-w-full max-h-[66vh] sm:max-h-[74vh] flex items-center justify-center"
          >
            {isVideo ? (
              <video
                src={currentPhotoUrl}
                controls
                autoPlay
                playsInline
                className="max-h-[66vh] sm:max-h-[74vh] max-w-full rounded-2xl sm:rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] object-contain border border-white/10 bg-black/50"
              />
            ) : (
              <img
                src={currentPhotoUrl}
                alt={memory.place || 'Recuerdo especial'}
                className="max-h-[66vh] sm:max-h-[74vh] max-w-full rounded-2xl sm:rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] object-contain border border-white/10"
              />
            )}
          </motion.div>

          {/* Botón Siguiente Flotante en los bordes (No invade la foto) */}
          {canGoNext && (
            <button
              onClick={handleNext}
              className="absolute right-2 sm:right-6 z-30 w-11 h-11 sm:w-13 sm:h-13 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/70 active:scale-95 text-white/90 hover:text-white backdrop-blur-xl border border-white/20 shadow-2xl transition-all cursor-pointer group"
              title="Siguiente foto (→)"
            >
              <ChevronRight className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
            </button>
          )}
        </main>

        {/* 3. BARRA INFERIOR FLOTANTE DE CRISTAL (DEDICATORIA E INFORMACIÓN) */}
        <footer className="relative z-30 w-full max-w-2xl mx-auto px-4 pb-4 sm:pb-6 pt-1">
          <div className="rounded-2xl sm:rounded-3xl bg-black/55 backdrop-blur-xl border border-white/15 p-3.5 sm:p-4 shadow-[0_12px_40px_rgba(0,0,0,0.5)] flex flex-col gap-2.5">
            {/* Si tiene varias fotos: carrusel de miniaturas */}
            {hasMultiplePhotos && (
              <div className="flex items-center justify-center gap-2 overflow-x-auto py-1 no-scrollbar">
                {mediaList.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSlideDirection(idx > photoIndex ? 'right' : 'left');
                      setPhotoIndex(idx);
                    }}
                    className={`relative w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden shrink-0 transition-all border cursor-pointer ${
                      idx === photoIndex
                        ? 'border-rose-400 scale-105 shadow-md ring-2 ring-rose-400/50 opacity-100'
                        : 'border-white/20 opacity-50 hover:opacity-90'
                    }`}
                  >
                    <img src={url} alt={`Miniatura ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Mensaje / Dedicatoria y Acciones */}
            <div className="flex items-center justify-between gap-3">
              {/* Mensaje de amor */}
              <div className="min-w-0 flex-1">
                {memory.message ? (
                  <p className="text-sm sm:text-base font-medium text-white/95 leading-relaxed line-clamp-2">
                    &ldquo;{memory.message}&rdquo;
                  </p>
                ) : (
                  <p className="text-sm font-medium text-slate-300">
                    Un instante inolvidable de nuestro camino juntos.
                  </p>
                )}

                {/* Info móvil (lugar y fecha) */}
                <div className="flex sm:hidden items-center gap-2 mt-1 text-[11px] text-slate-400 truncate">
                  {memory.place && (
                    <span className="flex items-center gap-1 text-rose-300 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="truncate">{memory.place}</span>
                    </span>
                  )}
                  {memory.date && (
                    <span className="flex items-center gap-1 text-amber-300">
                      <Calendar className="w-3 h-3 shrink-0" />
                      <span>{formatDateSpanish(memory.date)}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </footer>
      </motion.div>
    </AnimatePresence>
  );
};
